// Billing B4.3. The event start and end time lists, and the Saturday rule
// (B0.7a, D-0064). Times are 'HH:MM' text on the venue's own clock, never a
// Date: a Date would move with the browser's time zone.
// The server has the same rule: functions/helper/event-times.js. A test there
// runs this file and checks both give the same answers. No imports here, so
// that test can run it.

export const FIRST_START = '06:00';
export const LAST_START = '23:00';
export const FIRST_END = '06:30';
export const LAST_END = '23:30';
// D-0064: a Saturday event, clean-up included, ends by 11:00 PM unless the venue opens Sundays.
export const SATURDAY_LAST_END = '23:00';
const STEP_MINUTES = 30;

const TIME = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** Minutes after midnight, or null. */
export const toMinutes = (time) => {
  const match = typeof time === 'string' ? TIME.exec(time) : null;
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
};

const fromMinutes = (minutes) => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

const timesBetween = (first, last) => {
  const times = [];
  for (let m = toMinutes(first); m <= toMinutes(last); m += STEP_MINUTES) times.push(fromMinutes(m));
  return times;
};

export const START_TIMES = timesBetween(FIRST_START, LAST_START);
export const END_TIMES = timesBetween(FIRST_END, LAST_END);

export const isStartTime = (time) => START_TIMES.includes(time);
export const isEndTime = (time) => END_TIMES.includes(time);

/** '16:00' → '4:00 PM' (Braeden, 2026-10-31). */
export const timeLabel = (time) => {
  const minutes = toMinutes(time);
  if (minutes === null) return '';
  const hour = Math.floor(minutes / 60);
  return `${hour % 12 || 12}:${String(minutes % 60).padStart(2, '0')} ${hour < 12 ? 'AM' : 'PM'}`;
};

/** `day`: 'YYYY-MM-DD', read in UTC so no time zone moves it. */
export const isSaturday = (day) => new Date(`${day}T00:00:00Z`).getUTCDay() === 6;

/** The latest end time on the event's last day. */
export const lastEndTime = (endDay, sundaysOpen) => (!sundaysOpen && isSaturday(endDay) ? SATURDAY_LAST_END : LAST_END);

/** The start times to offer. One day: only times with an end time after them. */
export const startTimesFor = ({ start, end, sundaysOpen }) =>
  start === end ? START_TIMES.filter((t) => toMinutes(t) < toMinutes(lastEndTime(end, sundaysOpen))) : START_TIMES;

/** The end times to offer: up to the last end time; one day: after the start. */
export const endTimesFor = ({ start, end, startTime, sundaysOpen }) =>
  END_TIMES.filter(
    (t) =>
      toMinutes(t) <= toMinutes(lastEndTime(end, sundaysOpen)) &&
      (start !== end || !isStartTime(startTime) || toMinutes(t) > toMinutes(startTime))
  );

/** null when the times are good; else 'no-times', 'end-before-start' or 'saturday-late'. */
export const timesProblem = ({ start, end, startTime, endTime, sundaysOpen }) => {
  if (!isStartTime(startTime) || !isEndTime(endTime)) return 'no-times';
  if (start === end && toMinutes(endTime) <= toMinutes(startTime)) return 'end-before-start';
  if (toMinutes(endTime) > toMinutes(lastEndTime(end, sundaysOpen))) return 'saturday-late';
  return null;
};
